/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CircularProgress as MuiCircularProgress } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import useSWR from 'swr';
import { dataFetcher, endpoints } from 'Api';
import { Page as PageLoggedIn } from 'Components/page';
import { AppContext, AuthContext } from 'Context';
import { Pathname } from 'Routes';
import useMyAxios from 'hooks/useMyAxios';
import { getUserDetailsFromCode } from 'Api/apiFunctions';
import LightRays from 'Components/reactbits/LightRays';
import { Page } from '../components/page';
import styles from './index.module.css';
import { UploadModal } from './updateVideo';
import Plan from './plan';

const CircularProgress = withStyles({
  circle: {
    color: '#fff !important'
  }
})(MuiCircularProgress);

/* Same backdrop as the gift flow's plan step — near-black with the volumetric
   light rays fanning down from the top. Rendered behind the plans content. */
const PlansBackdrop = () => (
  <div className={styles.raysLayer} aria-hidden="true">
    <LightRays
      raysOrigin="top-center"
      raysColor="#ffffff"
      raysSpeed={0.9}
      lightSpread={0.85}
      rayLength={1.25}
      fadeDistance={1.1}
      saturation={1}
      followMouse
      mouseInfluence={0.08}
      noiseAmount={0.08}
      distortion={0.04}
    />
  </div>
);

/* Heading block shared by all three plan views. */
const PlansHeading = () => (
  <div className={styles.plansHeader}>
    <div className={styles.message}>Start Streaming Your Memories!</div>
    <p className={styles.messageSub}>Choose the plan that fits your memories.</p>
  </div>
);

export const Plans = ({ userIsSignedIn }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [planName, setPlanName] = useState();
  const { type, id } = useParams();
  const [contentId, setContentId] = useState(id);
  const [contentType, setContentType] = useState(type);
  const { setAppSnackbar, getSubscription, userDetails, currentSelectedPlan,
    setCurrentSelectedPlan, subscriptionDetails,
    setUserDetails, langText, referralStatus, allPlans, daysOfMonths } = useContext(AppContext);
  const { gettingSubscription, setToken: setUserToken } = useContext(AuthContext);
  useEffect(() => {
    getSubscription();
  }, []);
  const [uploadModalIsVisible, setUploadModalVisibility] = useState(false);
  const hideUploadModal = () => { setUploadModalVisibility(false); };
  const [plans, setPlans] = useState([]);
      const [isSwitchPlan, setIsSwitchPlan] = useState('1');
  const planText = langText?.settings?.billPayment?.planList;
  const [paymentToken, setToken] = useState();
  const [priceId, setPriceId] = useState();
  const [email, setEmail] = useState(userDetails?.email);
  const [trial_period_days, setTrial_period_days] = useState('0');
  const [myPlan, setMyPlan] = useState(subscriptionDetails?.current_plan);
  const [freePlanId, setFreePlanId] = useState(undefined);
  const emailCode = new URLSearchParams(location?.search)?.get('email');

  // Normal-user signup flow: the free-plan limit banner's "Upgrade to Premium"
  // button lands the brand-new user here. In that flow we must NOT expose the
  // full app chrome (header/sidebar lets them roam the app); show only the
  // plans + a Back button that returns them to /gift-content. Keyed off the
  // same refresh-proof marker the gift-content upload flow uses.
  const isNewUserUploadFlow = (() => {
    try {
      return location?.state?.newUserUpload === true
        || sessionStorage.getItem('newUserUploadFlow') === 'true';
    } catch (e) { return false; }
  })();

  const handleBackToGiftContent = () => {
    navigate(Pathname.giftFlow.content, { state: { newUserUpload: true } });
  };

  useEffect(() => {
    setMyPlan(subscriptionDetails?.current_plan);
  }, [subscriptionDetails]);

  const {
    response: userDetFrmCode,
    error: userDetFrmCodeErr,
    loading: userDetFrmCodeLoading,
    refetch: refetchuserDetFrmCode,
  } = useMyAxios(getUserDetailsFromCode, { code: emailCode }, null, emailCode, emailCode ? [emailCode] : []);

  useEffect(() => {
    if (!userDetFrmCodeLoading && userDetFrmCode && userDetFrmCode?.data) {
      setUserDetails({ ...userDetFrmCode?.data });
      setUserToken(userDetFrmCode?.data?.token);
      window.location.replace(Pathname.plans);
    }
  }, [userDetFrmCode, userDetFrmCodeLoading]);

  const { mutate: getPlans } = useSWR([endpoints.getAllActivePlans], {
    fetcher: (url) => dataFetcher(url),
    onSuccess: ({ success, subscriptions, data }) => {
      if (success) {
        if (referralStatus && parseInt(referralStatus, 10) === 1 && parseInt(subscriptionDetails?.is_skip, 10) !== 1 && !subscriptionDetails?.paymentMethods?.length) {
          setPlans([...data, subscriptionDetails?.subscription]);
          setFreePlanId(subscriptionDetails?.subscription?.id);
        } else {
          setPlans(data);
          setFreePlanId(undefined);
        }
      } else {
        setPlans([]);
      }
    },
    onError: () => { setPlans([]); },
  });
  useEffect(() => {
    if (subscriptionDetails?.subscription) {
      getPlans();
    }
  }, [referralStatus, subscriptionDetails]);

  // Signed-in plans list — reused by the normal PageLoggedIn view and the
  // stripped-down new-user signup view (both show the same signed-in plans).
  const signedInPlansList = allPlans && allPlans?.length > 0 && allPlans?.filter((obj, i) => obj?.hide !== isSwitchPlan)?.map((plan, index) => (
    <Plan
      isSwitchPlan={isSwitchPlan}
      setIsSwitchPlan={setIsSwitchPlan}
      product_name={plan?.product_name}
      sub_title={plan?.sub_title}
      hideToggle={plan?.hide}
      needToggle={allPlans?.some((plan) => plan?.interval_in_days === daysOfMonths)}
      // name={plan.name === 'Extended' ? 'Premium' : plan.name}
      // isSuggested={plan.name !== 'Standard'}
      amount={`${plan?.amount}`}
      description={plan?.description}
      price_description={plan?.price_description}
      viewers={plan?.is_unlimited === '1' ? (planText?.unlimited || 'unlimited') : plan?.max_view_request}
      priceDetails={planText?.userMonth || 'user / month'}
      storage={plan?.max_storage_text}
      price_id={plan?.price_id}
      setToken={setToken}
      setPriceId={setPriceId}
      setTrial_period_days={setTrial_period_days}
      setPlanName={setPlanName}
      userIsSignedIn
      isExtended={plan?.name !== 'Standard'}
      benefit={plan?.is_addon_allowed === '1'
        ? `Add ${plan?.max_add_on_allowed} GB of storage for
      $${(plan?.addon_rate / 100).toFixed(2)}/month` : null}
      currentSelectedPlan={currentSelectedPlan}
      setCurrentSelectedPlan={setCurrentSelectedPlan}
      hasTrial={plan?.has_trial}
      totalTrialDays={plan?.total_trial_days}
      planId={plan?.id}
      contentType={contentType}
      contentId={contentId}
      freePlanId={freePlanId}
      color={index % 2 === 0 ? 'white' : 'blue'}
    />
  ));

  // Stripped-down plans view for the normal-user signup flow: no header /
  // sidebar / bottom bar (so the brand-new user can't roam the app), just the
  // plans and a Back button that returns them to /gift-content.
  if (isNewUserUploadFlow) {
    return (
      <div className={styles.newUserPlansPage}>
        <PlansBackdrop />
        {/* Same BACK link style/position as the /gift-content page. */}
        <div
          aria-hidden="true"
          className={`${styles.backLink} ${styles.backLinkNewUser}`}
          onClick={handleBackToGiftContent}
        >
          BACK
        </div>
        {gettingSubscription ? (
          <div className={styles.newUserPlansLoader}><CircularProgress /></div>
        ) : (
          <div className={styles.planContainer}>
            <PlansHeading />
            <div className={styles.plansList}>
              {signedInPlansList}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {
        userIsSignedIn
          ? (
            /* noHeader: no app nav on the plans screen — it's a focused
               plan-picker, like the gift flow's plan step. */
            <PageLoggedIn
              className={styles.page}
              containerClassName={styles.pageContainer}
              contentClassName={styles.pageContent}
              noBottomBar
              noDefaultBg
              noFixedSidebar
              noHeader
              transparentCompactHeader
            >
              <UploadModal isVisible={uploadModalIsVisible} onHide={hideUploadModal} contentType={contentType} />
              <PlansBackdrop />
              <div className={styles.planContainer}>
                <PlansHeading />
                <div className={styles.plansList}>
                  {signedInPlansList}
                  {/* <Plan name="Standard" price="4.95" storage="500 GB" viewers="10" priceDetails="user / month" />
          <Plan name="Premium" price="9.95" storage="1 TB" viewers="unlimited" isSuggested /> */}
                </div>
              </div>
            </PageLoggedIn>
          )
          : (

            gettingSubscription ? <CircularProgress /> : (
              <Page>
                <PlansBackdrop />
                <div className={styles.planContainer}>
                  <PlansHeading />
                  <div className={styles.plans}>
                    {allPlans && allPlans.length > 0 && allPlans?.filter((obj, i) => obj?.hide !== isSwitchPlan)?.map((plan, index) => (
                      <Plan
                        isSwitchPlan={isSwitchPlan}
                        needToggle={allPlans?.some((plan) => plan?.interval_in_days === daysOfMonths)}
                        product_name={plan?.product_name}
                        sub_title={plan?.sub_title}
                        amount={`${plan?.amount}`}
                        hideToggle={plan?.hide}
                        setIsSwitchPlan={setIsSwitchPlan}
                        description={plan?.description}
                        price_description={plan?.price_description}
                        viewers={plan?.is_unlimited === '1' ? (planText?.unlimited || 'unlimited') : plan?.max_view_request}
                        priceDetails={planText?.userMonth || 'user / month'}
                        storage={plan?.max_storage_text}
                        price_id={plan?.price_id}
                        setToken={setToken}
                        setPriceId={setPriceId}
                        setEmail={setEmail}
                        setTrial_period_days={setTrial_period_days}
                        setPlanName={setPlanName}
                        userIsSignedIn={false}
                        currentSelectedPlan={currentSelectedPlan}
                        benefit={`Add ${plan?.max_add_on_allowed} GB of storage for 
                        $${(plan?.addon_rate / 100).toFixed(2)}/month`}
                        setCurrentSelectedPlan={setCurrentSelectedPlan}
                        hasTrial={plan?.has_trial}
                        totalTrialDays={plan?.total_trial_days}
                        planId={plan?.id}
                        contentType={contentType}
                        contentId={contentId}
                        freePlanId={freePlanId}
                        color={index % 2 === 0 ? 'white' : 'blue'}
                      />
                    ))}
                    {/* <Plan name="Standard" price="4.95" storage="500 GB" viewers="10" priceDetails="user / month" />
            <Plan name="Premium" price="9.95" storage="1 TB" viewers="unlimited" isSuggested /> */}
                  </div>
                </div>
              </Page>
            )
          )
      }
    </>
  );
};
